/**
 * Case-view action router (#746 Wave 0 0b). Every case action is an id the page can open: from `?action=<id>`
 * (notification links, queue rows, Riley's links), from the ⋯ menu, or from a key. Behaviour lives in a definition
 * registered here, so a later slice adds an `actions/<name>.action.ts(x)` file (picked up by `actions/index.ts`)
 * instead of editing `case-view/index.tsx`. Registering an id again replaces the definition, which is how a slice
 * swaps the pane fallback for its real dialog. Opening an action only opens a surface; nothing is written, and every
 * customer email or Desk close still waits for a person's confirm.
 */
import type * as React from 'react'
import type { BrandFeature } from '../../../lib/api/generated'
import { hasBrandFeatures } from '../../../lib/brand-features'
import type { UpdatePrefill } from '../../case-update/prefill'
import type { CaseView } from '../api'

/** `?action=` values the case route accepts. The route validates against this list. */
export const CASE_ACTION_IDS = [
  'link',
  'time',
  'take',
  'update',
  'reply',
  'snooze',
  'chase_customer',
  'chase_external',
  'follow_up_owner',
  'close',
  'dispatch',
  'handoff',
  'move_ticket',
  'move_turn',
  'dismiss_callback',
  'no_reply_needed',
  'needs_reply',
  'change_contact',
  'reopen',
  'email_customer',
] as const
export type CaseActionId = (typeof CASE_ACTION_IDS)[number]

export function isCaseActionId(value: unknown): value is CaseActionId {
  return typeof value === 'string' && (CASE_ACTION_IDS as readonly string[]).includes(value)
}

/** What a definition may do to the page. The case view supplies it; definitions never touch its state directly. */
export interface CaseActionContext {
  c: CaseView
  /** Enabled features for this case's brand, resolved from ``MeOut.brands`` by the case screen. */
  features: ReadonlySet<BrandFeature>
  role: string | undefined
  /** Every role except billing (billing writes nothing to Desk). */
  canUpdate: boolean
  /** The simple UI mode hides scheduling and the dispatcher tools. */
  simple: boolean
  /** The caller may link, merge and split tickets. */
  dispatcher: boolean
  openUpdate: (prefill?: UpdatePrefill) => void
  openSchedule: () => void
  openSnooze: () => void
  openLink: () => void
  openTime: () => void
  /** Focus the composer of the case's customer pane. */
  reply: () => void
  /** Draft the chase in the pane for this audience; false when the case has no such pane. */
  startChase: (audience: 'customer' | 'contractor') => boolean
  /** #928 classify the exact newest customer turn; this POST is audited and refreshes the lane. */
  setReplyClassification: (classification: 'needs_reply' | 'no_reply_needed') => void
  /** Mount the registered `Dialog` of this action; `initial` is handed to it (e.g. the ticket a chip names). */
  openDialog: (id: CaseActionId, initial?: unknown) => void
  notify: (message: string) => void
  /** #1107: the customer pane's send state offers Email the customer (alert, robot or unconfirmed voicemail). */
  emailCustomerOffered?: boolean
  /** #1071: staff reopen. Optional so a caller that only opens a dialog can omit it. */
  reopenCase?: () => void
}

export interface CaseActionDialogProps {
  c: CaseView
  onClose: () => void
  /** Whatever `openDialog(id, initial)` passed. */
  initial?: unknown
}

export type CaseMenuGroup = 'work' | 'status' | 'case'

export interface CaseActionDef {
  id: CaseActionId
  /** Every listed feature must be enabled for this case's brand. */
  features?: readonly BrandFeature[]
  /** Backends this action can safely run against. Omit for a backend-neutral case action. */
  backends?: readonly CaseView['backend'][]
  /** Open the action. Called once per arrival or click. */
  run: (ctx: CaseActionContext) => void
  /**
   * A ⋯ menu item. Omit for an action that has its own button or key. `group` places it: `work` (doing and
   * communicating), `status` (who owns it and where it goes next) or `case` (the "Tickets & case" submenu, which
   * restructures tickets and cases). Items sort by `order` within a group (default 100), then registration order.
   */
  menu?: {
    label: string
    keys?: string
    group: CaseMenuGroup
    order?: number
    visible?: (ctx: CaseActionContext) => boolean
  }
  /** A dialog the page mounts while `ctx.openDialog(id)` is active. */
  Dialog?: React.ComponentType<CaseActionDialogProps>
}

const definitions = new Map<CaseActionId, CaseActionDef>()

export function registerCaseAction(def: CaseActionDef): void {
  definitions.set(def.id, def)
}

export function getCaseAction(id: CaseActionId): CaseActionDef | undefined {
  return definitions.get(id)
}

/** Registered definitions in registration order. */
export function listCaseActions(): CaseActionDef[] {
  return [...definitions.values()]
}

/** The visible ⋯ menu items of one group, in `order` then registration order. */
export function caseMenuActions(group: CaseMenuGroup, ctx: CaseActionContext): CaseActionDef[] {
  return listCaseActions()
    .filter((def) => def.menu?.group === group && supportsCase(def, ctx) && (def.menu.visible?.(ctx) ?? true))
    .sort((a, b) => (a.menu?.order ?? 100) - (b.menu?.order ?? 100))
}

/** Run an action by id; false if nothing is registered for it. */
export function runCaseAction(id: CaseActionId, ctx: CaseActionContext): boolean {
  const def = definitions.get(id)
  if (!def || !supportsCase(def, ctx)) return false
  def.run(ctx)
  return true
}

function supportsCase(def: CaseActionDef, ctx: CaseActionContext): boolean {
  return (!def.backends || def.backends.includes(ctx.c.backend ?? 'desk')) && hasBrandFeatures(ctx.features, def.features)
}
